"use client";

import { useEffect, useRef, useState, useTransition } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import {
  toggleEventHidden,
  updateEventDetails,
  uploadEventImage,
  importImageFromUrl,
} from "@/app/admin/actions";
import { Event } from "@/lib/types";
import { getEventImage } from "@/lib/utils";
import { useModalZIndex } from "@/lib/use-modal-z-index";
import { AppQueryProvider } from "@/app/providers";

interface EventAdminModalProps {
  event: Event;
  onClose: () => void;
}

export function EventAdminModal(props: EventAdminModalProps) {
  return <AppQueryProvider><EventAdminModalContent {...props} /></AppQueryProvider>;
}

function EventAdminModalContent({ event, onClose }: EventAdminModalProps) {
  const zIndex = useModalZIndex();
  const [isPending, startTransition] = useTransition();
  const [eventName, setEventName] = useState(event.name);
  const [imageUrl, setImageUrl] = useState(getEventImage(event) ?? "");
  const [imageFocus, setImageFocus] = useState(event.image_focus ?? "center");
  const [localDate, setLocalDate] = useState(event.local_date ?? "");
  const [localTime, setLocalTime] = useState(event.local_time ?? "");
  const [priceMin, setPriceMin] = useState(event.price_min?.toString() ?? "");
  const [priceMax, setPriceMax] = useState(event.price_max?.toString() ?? "");
  const [isUploading, setIsUploading] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const router = useRouter();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const handleHide = () => {
    startTransition(async () => {
      await toggleEventHidden(event.id, true);
      queryClient.invalidateQueries({ queryKey: ["admin", "events"] });
      router.refresh();
      onClose();
    });
  };

  const handleUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("eventId", String(event.id));
      const publicUrl = await uploadEventImage(formData);
      setImageUrl(publicUrl);
    } catch (err) {
      console.error("Upload failed:", err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleImportUrl = async () => {
    const url = importUrl.trim();
    if (!url) return;
    setIsUploading(true);
    try {
      const publicUrl = await importImageFromUrl(url, "events");
      setImageUrl(publicUrl);
      setImportUrl("");
    } catch (err) {
      console.error("Import from URL failed:", err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = () => {
    startTransition(async () => {
      await updateEventDetails(event.id, {
        name: eventName.trim() || event.name,
        image_url: imageUrl.trim() || null,
        image_focus: imageFocus,
        local_date: localDate.trim() || null,
        local_time: localTime.trim() || null,
        price_min: priceMin ? parseFloat(priceMin) : null,
        price_max: priceMax ? parseFloat(priceMax) : null,
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "events"] });
      router.refresh();
      onClose();
    });
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/80 px-4 backdrop-blur-sm"
      style={{ zIndex }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md flex flex-col overflow-visible border border-neutral-800 bg-surface"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Corner markers */}
        <div className="absolute -top-1 -left-1 h-3 w-3 border-t border-l border-acid" />
        <div className="absolute -top-1 -right-1 h-3 w-3 border-t border-r border-acid" />
        <div className="absolute -bottom-1 -left-1 h-3 w-3 border-b border-l border-acid" />
        <div className="absolute -bottom-1 -right-1 h-3 w-3 border-b border-r border-acid" />

        {/* Header */}
        <div className="flex items-center justify-between gap-4 border-b border-neutral-800 p-5">
          <input
            type="text"
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            className="min-w-0 flex-1 truncate bg-transparent font-mono text-sm font-bold tracking-wider text-white outline-none border-b border-transparent focus:border-acid/50"
          />
          <button
            onClick={onClose}
            className="shrink-0 text-neutral-500 transition-colors hover:text-white"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[70vh] p-5 space-y-5">
          {/* Image */}
          <div>
            <h3 className="mb-2 font-mono text-[10px] font-bold uppercase tracking-widest text-neutral-500">
              Image
            </h3>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleUpload(file);
                e.target.value = "";
              }}
            />
            {/* Preview with upload overlay */}
            <div className="group relative aspect-video w-full overflow-hidden rounded border border-neutral-700 bg-neutral-900">
              {imageUrl ? (
                /* eslint-disable-next-line @next/next/no-img-element */
                <img
                  src={imageUrl}
                  alt="Preview"
                  className="h-full w-full object-cover"
                  style={{ objectPosition: imageFocus }}
                />
              ) : (
                <div className="flex h-full items-center justify-center">
                  <span className="font-mono text-xs text-neutral-600">NO IMAGE</span>
                </div>
              )}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 transition-opacity group-hover:opacity-100 disabled:cursor-not-allowed"
              >
                <span className="font-mono text-[10px] font-bold text-white">
                  {isUploading ? "UPLOADING..." : "UPLOAD IMAGE"}
                </span>
              </button>
            </div>
            <div className="mt-1.5 flex gap-1">
              <input
                type="text"
                value={importUrl}
                onChange={(e) => setImportUrl(e.target.value)}
                placeholder="Paste image URL to import..."
                className="min-w-0 flex-1 rounded border border-neutral-700 bg-dark px-2 py-1 font-mono text-[10px] text-neutral-300 placeholder-neutral-600 outline-none focus:border-acid/50"
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); handleImportUrl(); }
                }}
              />
              <button
                onClick={handleImportUrl}
                disabled={isUploading || !importUrl.trim()}
                className="shrink-0 rounded border border-neutral-700 px-2 py-1 font-mono text-[10px] font-bold text-neutral-400 transition-colors hover:text-white disabled:opacity-40"
              >
                {isUploading ? "..." : "IMPORT"}
              </button>
            </div>
            {/* Focus buttons */}
            <div className="mt-1.5 flex gap-1">
              {(["top", "center", "bottom"] as const).map((pos) => (
                <button
                  key={pos}
                  onClick={() => setImageFocus(pos)}
                  className={`flex-1 rounded px-2 py-1 font-mono text-[10px] font-bold transition-colors ${
                    imageFocus === pos
                      ? "bg-acid text-black"
                      : "border border-neutral-700 text-neutral-400 hover:text-white"
                  }`}
                >
                  {pos.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Date / Time / Price */}
          <div>
            <h3 className="mb-2 font-mono text-[10px] font-bold uppercase tracking-widest text-neutral-500">
              Details
            </h3>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <label className="shrink-0 w-12 font-mono text-[10px] text-neutral-500">DATE</label>
                <input
                  type="date"
                  value={localDate}
                  onChange={(e) => setLocalDate(e.target.value)}
                  className="min-w-0 flex-1 rounded border border-neutral-700 bg-dark px-2 py-1 font-mono text-[11px] text-neutral-300 outline-none focus:border-acid/50 [color-scheme:dark]"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="shrink-0 w-12 font-mono text-[10px] text-neutral-500">TIME</label>
                <input
                  type="time"
                  value={localTime}
                  onChange={(e) => setLocalTime(e.target.value)}
                  className="min-w-0 flex-1 rounded border border-neutral-700 bg-dark px-2 py-1 font-mono text-[11px] text-neutral-300 outline-none focus:border-acid/50 [color-scheme:dark]"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="shrink-0 w-12 font-mono text-[10px] text-neutral-500">PRICE</label>
                <input
                  type="number"
                  value={priceMin}
                  onChange={(e) => setPriceMin(e.target.value)}
                  placeholder="Min"
                  step="0.01"
                  className="min-w-0 flex-1 rounded border border-neutral-700 bg-dark px-2 py-1 font-mono text-[11px] text-neutral-300 placeholder-neutral-600 outline-none focus:border-acid/50"
                />
                <span className="font-mono text-[10px] text-neutral-600">&ndash;</span>
                <input
                  type="number"
                  value={priceMax}
                  onChange={(e) => setPriceMax(e.target.value)}
                  placeholder="Max"
                  step="0.01"
                  className="min-w-0 flex-1 rounded border border-neutral-700 bg-dark px-2 py-1 font-mono text-[11px] text-neutral-300 placeholder-neutral-600 outline-none focus:border-acid/50"
                />
              </div>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="flex gap-2 border-t border-neutral-800 p-5">
          <button
            onClick={handleHide}
            disabled={isPending}
            className="rounded px-3 py-2.5 font-mono text-xs font-bold transition-colors cursor-pointer disabled:opacity-40 border border-red-500/30 text-red-500 hover:bg-red-500/10"
          >
            HIDE
          </button>
          <button
            onClick={handleSave}
            disabled={isPending}
            className="flex-1 rounded bg-acid py-2.5 font-mono text-sm font-bold text-black transition-opacity cursor-pointer hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isPending ? "SAVING..." : "SAVE"}
          </button>
        </div>
      </div>
    </div>
  );
}
